import { Component, Inject, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { L10N_LOCALE, L10nLocale } from 'angular-l10n';
import {
  GlobalTenantContractsApi,
  TenantContractModel,
  TenantContractsApi,
  FieldMetadata,
  FilterMode,
  FilterType,
} from 'src/app/usage-api';
import { ConfirmComponent, ConfirmData } from 'src/app/shared/modals/confirm/confirm.component';
import { MatDialog } from '@angular/material/dialog';
import { SB_PANEL_MESSAGE, SnackbarService } from 'src/app/shared/snackbar.service';
import { ApiError } from 'src/app/shared/model/utility';
import {
  catchError,
  delay,
  filter,
  map,
  of,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
  throwError,
  finalize,
} from 'rxjs';
import { ContractsDataSource } from '../../data-table/datasource/contracts-datasource';
import { FileUploadHelper } from '../../model/fileupload-helper';
import { Filter, FilterField } from '../../filtered-search/filtered-search.component';
import { AuthService } from '../../authentication/auth.service';
import { DefaultDateRanges, isDateRange } from '../../model/date.helper';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';

@Component({
  selector: 'app-contract-list-view',
  templateUrl: './contract-list-view.component.html',
  styleUrls: ['./contract-list-view.component.scss'],
  standalone: false,
})
export class ContractListViewComponent implements OnInit, OnDestroy {
  dataSource?: ContractsDataSource;
  title = 'BackOffice.ManageContracts.TenantContracts';
  pageSize: number = 25;
  additionalColumns: string[] = ['delete'];

  apiWaitMs: number = 1000;
  loading = signal<boolean>(false);

  filters: FilterField[] = [];
  defaultFilters: Filter[] = [];
  private readonly destroy$ = new Subject<void>();

  constructor(
    @Inject(L10N_LOCALE) public locale: L10nLocale,
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly tenantContractsApi: TenantContractsApi,
    private readonly globalTenantContractsApi: GlobalTenantContractsApi,
    private readonly authService: AuthService,
    private readonly sb: SnackbarService,
    private readonly route: ActivatedRoute,
  ) {}

  ngOnInit() {
    this.dataSource = new ContractsDataSource(
      this.globalTenantContractsApi,
      this.tenantContractsApi,
      this.authService,
      undefined as unknown as MatPaginator,
      undefined as unknown as MatSort,
      {},
      false,
    );

    if (this.route.snapshot.queryParams['isActive'] !== undefined) {
      this.defaultFilters.push({ field: 'isActive', value: this.route.snapshot.queryParams['isActive'] === 'true' });
      this.updateDataSourceField('isActive', this.route.snapshot.queryParams['isActive'] === 'true');
    } else {
      this.defaultFilters.push({ field: 'isActive', value: true });
      this.updateDataSourceField('isActive', true);
    }

    this.dataSource.metadata$.pipe(filter(Boolean), take(1), takeUntil(this.destroy$)).subscribe((metadata) => {
      const metadataFilters = [...(metadata?.fields ?? [])]
        ?.filter((f) => f.properties?.filterable === true)
        .sort((a, b) => a.displayName?.localeCompare(b.displayName ?? '') ?? 0)
        .map((m) => this.metadataToFilter(m))
        .filter((f): f is FilterField => f !== null);

      this.filters = [...(metadataFilters ?? [])];
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  metadataToFilter(field: FieldMetadata): FilterField | null {
    if (this.filters.some((f) => f.field === field.key || f.field === field.field)) {
      return null;
    }

    const fieldKey = field.key ?? '';
    const localizedLabel = this.getFilterLabel(fieldKey, field);

    if (fieldKey === 'brand') {
      return {
        field: fieldKey,
        localizedLabel,
        type: 'select',
        values: ['CivicReady', 'Konexus', 'AlertSense'],
      };
    }

    if (fieldKey === 'tenantId') {
      return {
        field: fieldKey,
        localizedLabel,
        type: 'tenant',
      };
    }

    switch (field.type) {
      case 'date':
        return {
          field: fieldKey,
          localizedLabel,
          type: 'daterange-menu',
          values: DefaultDateRanges,
        };

      case 'boolean':
        return {
          field: fieldKey,
          localizedLabel,
          type: 'boolean',
        };

      case 'long':
      case 'integer':
      case 'scaled_float':
      case 'float':
      case 'double':
      case 'keyword':
      case 'text':
      default:
        return {
          field: fieldKey,
          localizedLabel,
          type: 'text',
        };

    }
  }

  private getFilterLabel(fieldKey: string, field: FieldMetadata): string {
    const localizationMap: Record<string, string> = {
      tenantName: 'BackOffice.ManageContracts.Contract.TenantName',
      tenantId: 'BackOffice.ManageContracts.Contract.TenantId',
      brand: 'BackOffice.ManageContracts.Contract.Brand',
      contractStartDate: 'BackOffice.ManageContracts.Contract.ContractStartDate',
      servicePeriodEndDate: 'BackOffice.ManageContracts.Contract.ServiceEndDate',
      isActive: 'BackOffice.ManageContracts.Contract.Active',
    };

    return localizationMap[fieldKey] || field.displayName || fieldKey;
  }

  onFilterChange(filter: Filter) {
    this.updateDataSourceField(filter.field, filter.value);
  }

  updateDataSourceField(field: string, value: unknown) {
    if (!this.dataSource) {
      return;
    }

    if (field === 'isActive') {
      this.dataSource.filters.isActive$.next(value === undefined || value === null ? undefined : value === 'true' || value === true);
      return;
    } 

    this.dataSource.updateFilter(
      field,
      this.convertFilterValue(value),
      FilterMode.Includes,
      isDateRange(value) ? FilterType.DateRange : FilterType.Values,
    );
  }

  private convertFilterValue(value: unknown): string[] | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (typeof value === 'string') {
      return value.split('||').map((v) => v.trim());
    }

    if (isDateRange(value)) {
      return [value.start?.toISOString() ?? '', value.end?.toISOString() ?? ''];
    }

    if (typeof value === 'boolean') {
      return [value.toString()];
    }

    return [String(value)];
  }

  onRowClicked(contract: TenantContractModel) {
    void this.router.navigate(['/back-office/contracts', contract.tenantId, contract.id]);
  }

  newContractClick() {
    void this.router.navigate(['back-office', 'contracts', 'create']);
  }

  confirmDeleteContract(contract: TenantContractModel) {
    const dialogRef = this.dialog.open(ConfirmComponent, {
      width: '600px',
      data: {
        title: `Delete Contract: ${contract.tenantName}`,
        message: 'Are you sure you want to delete this contract?',
        requireInput: true,
        inputPhrase: 'DELETE',
        inputPlaceholder: 'Enter <strong>DELETE</strong> to confirm',
      } as ConfirmData,
    });

    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.deleteContract(contract);
      }
    });
  }

  exportContracts() {
    this.loading.set(true);
    this.globalTenantContractsApi
      .globalTenantContractsExportContracts({})
      .pipe(
        switchMap((result) => FileUploadHelper.download(result, `${new Date().toISOString()}-contracts.csv`)),
        finalize(() => this.loading.set(false)),
        catchError((error) => {
          this.displayErrorMessage('Error exporting contracts');
          return throwError(() => error);
        }),
      )
      .subscribe();
  }

  uploadContracts() {
    this.loading.set(true);
    FileUploadHelper.upload<Blob>('.csv', (content) => new Blob([content], { type: 'text/csv' }))
      .pipe(
        filter((file) => file.type === 'complete'),
        map((file) => file.content),
        switchMap((file) => {
          return this.globalTenantContractsApi.globalTenantContractsImportContracts(file!).pipe(
            delay(this.apiWaitMs),
            catchError((error) => {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
              this.displayErrorMessage(error?.error?.errors?.join(', ') ?? 'Error uploading contracts');
              return throwError(() => error);
            }),
            tap((result) => {
              if (result?.isSuccess) {
                this.dataSource?.loadData(true);
                this.displayStatusMessage(result.message ?? 'Contracts uploaded');
              } else {
                this.displayErrorMessage(result?.errors?.join(', ') ?? 'Error uploading contracts');
              }
            }),
          );
        }),
        finalize(() => this.loading.set(false)),
      )
      .subscribe();
  }

  deleteContract(contract: TenantContractModel) {
    this.tenantContractsApi
      .tenantContractsRemoveContract(contract.tenantId!, contract.id!)
      .pipe(
        delay(this.apiWaitMs),
        map((_) => {
          this.dataSource?.loadData(true);
          this.displayStatusMessage('Contract removed');
        }),
        catchError((_: ApiError) => {
          this.displayErrorMessage('Error deleting contract');
          return of(null);
        }),
      )
      .subscribe();
  }

  onDeleteClick(event: Event, contract: TenantContractModel) {
    event.stopPropagation();
    this.confirmDeleteContract(contract);
  }

  displayStatusMessage(message: string) {
    this.sb.open(message, undefined, { duration: 3000, panelClass: SB_PANEL_MESSAGE });
  }

  displayErrorMessage(message: string) {
    this.sb.open(message, undefined, { duration: 3000, panelClass: SB_PANEL_MESSAGE });
  }
}
